import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Globe, Building2, MapPin, User, ShieldCheck, Loader2, Smartphone, Mail, Send, Palette, Sun, Moon, Check } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { useTheme, PALETTES } from './ThemeContext';

const PersonalInformationModal = ({ isOpen, onClose }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
    const { mode, palette, setMode, setPalette } = useTheme();
    const [loading, setLoading] = useState(false);
    const [setupLoading, setSetupLoading] = useState(false); // 🎯 لودينج خاص بإعداد الـ 2FA
    const [orgs, setOrgs] = useState([]);
    const [sites, setSites] = useState([]);
    const [qrUri, setQrUri] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [setupMode, setSetupMode] = useState(false);
    // 🎯 نحفظ الثيم الأصلي وقت فتح المودال عشان نرجعله لو اتلغي/اتقفل من غير حفظ
    const originalThemeRef = useRef({ mode: 'light', palette: 'teknora' });
    // 🎯 المؤسسة والموقع دول بيانات صلاحية (بيتحكموا في نطاق البيانات اللي
    // اليوزر يشوفها) مش بيانات بروفايل شخصي عادية - يوزر عادي ميقدرش ينقل
    // نفسه لمؤسسة تانية بنفسه، ده لازم يتم بمعرفة أدمن من شاشة المستخدمين
    // 🎯 fallback على اسم الرول لجلسات قديمة اتعملها login قبل ما is_super_admin
    // يترجع من الباك إند
    const currentUserRole = JSON.parse(localStorage.getItem('user') || '{}')?.role;
    const isSuperAdmin = currentUserRole?.is_super_admin === true || currentUserRole?.name === 'SUPER_ADMIN';

    const [formData, setFormData] = useState({
        display_name: '',
        email: '',
        org_id: '',
        site_id: '',
        language: 'en',
        is_2fa_enabled: false,
        two_factor_method: 'none',
        theme_mode: 'light',
        theme_palette: 'teknora'
    });

    useEffect(() => {
        if (isOpen) {
            const user = JSON.parse(localStorage.getItem('user'));
            setFormData({
                display_name: user?.display_name || '',
                email: user?.email || '',
                org_id: user?.org_id || '',
                site_id: user?.site_id || '',
                language: user?.language || 'en',
                is_2fa_enabled: user?.is_2fa_enabled || false,
                two_factor_method: user?.two_factor_method || 'none',
                theme_mode: mode,
                theme_palette: palette
            });
            originalThemeRef.current = { mode, palette };
            fetchOrgs();
            setSetupMode(false);
            setQrUri('');
            setVerificationCode('');
        }
    }, [isOpen]);

    // 🎯 معاينة فورية: أي تغيير في المودال يتطبق على الشاشة كلها على طول
    const applyPalette = (id) => {
        setPalette(id);
        setFormData(prev => ({ ...prev, theme_palette: id }));
    };
    const applyMode = (next) => {
        setMode(next);
        setFormData(prev => ({ ...prev, theme_mode: next }));
    };

    const handleClose = () => {
        // 🎯 لو المستخدم قفل/ألغى من غير حفظ، نرجّع الثيم اللي كان شغال قبل ما يفتح المودال
        setMode(originalThemeRef.current.mode);
        setPalette(originalThemeRef.current.palette);
        onClose();
    };

    const fetchOrgs = async () => {
        try {
            const res = await axios.get('/api/organizations');
            setOrgs(res.data);
        } catch (error) {
            console.error("Failed to fetch Orgs", error);
        }
    };

    useEffect(() => {
        if (formData.org_id) {
            axios.get(`/api/sites/${formData.org_id}`)
                .then(res => {
                    const data = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
                    setSites(data);
                })
                .catch(err => console.error("Failed to fetch sites", err));
        }
    }, [formData.org_id]);

    // 🎯 دالة تهيئة الـ 2FA (بتدعم الإيميل وجوجل مع بعض)
    const handleSetup2FA = async () => {
        if (formData.two_factor_method === 'email' && !formData.email) {
            toast.error(t('personalInfo.emailRequiredFor2FA'));
            return;
        }

        setSetupLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post('/api/user/2fa/setup', 
                { method: formData.two_factor_method }, 
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (formData.two_factor_method === 'authenticator') {
                setQrUri(res.data.qr_uri);
            } else {
                toast.success(t('personalInfo.otpSent'));
            }

            setSetupMode(true);
        } catch (err) {
            toast.error(t('personalInfo.failedQR', 'Failed to setup 2FA'));
        } finally {
            setSetupLoading(false);
        }
    };

    // 🎯 دالة تأكيد الـ 2FA وتفعيله
    const handleVerify2FA = async () => {
        setSetupLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/user/2fa/verify-setup', 
                { code: verificationCode, method: formData.two_factor_method },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            // تحديث حالة المستخدم في المتصفح عشان متطيرش
            const updatedUser = { ...JSON.parse(localStorage.getItem('user')), is_2fa_enabled: true, two_factor_method: formData.two_factor_method };
            localStorage.setItem('user', JSON.stringify(updatedUser));

            setFormData({ ...formData, is_2fa_enabled: true });
            setSetupMode(false);
            setVerificationCode('');
            toast.success(t('personalInfo.setupSuccess'));
        } catch (err) {
            toast.error(t('personalInfo.invalidCode'));
        } finally {
            setSetupLoading(false);
        }
    };

    // 🎯 حفظ باقي بيانات البروفايل
    const handleSave = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token'); 
            await axios.post('/api/user/update-profile', formData, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const updatedUser = { ...JSON.parse(localStorage.getItem('user')), ...formData };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            
            toast.success(t('personalInfo.updateSuccess')); 
            setTimeout(() => window.location.reload(), 500);
            
        } catch (err) {
            if (err.response?.status === 401) {
                toast.error(t('personalInfo.sessionExpired')); 
            } else {
                toast.error(t('personalInfo.updateFailed')); 
            }
        } finally {
            setLoading(false);
        }
    };
  
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="bg-surface-card w-full max-w-2xl rounded-sm shadow-2xl border-t-4 border-primary overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="p-4 border-b border-line flex justify-between items-center bg-surface-panel flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <User size={18} className="text-primary" />
                        <span className="font-black text-[12px] text-primary uppercase tracking-widest">{t('personalInfo.title')}</span>
                    </div>
                    <button onClick={handleClose} className="text-ink-secondary hover:text-status-danger transition-colors"><X size={20}/></button>
                </div>

                {/* Body */}
                <div className="p-8 grid grid-cols-2 gap-8 overflow-y-auto custom-scrollbar">
                    
                    <div className="col-span-2 grid grid-cols-2 gap-6 bg-primary-soft p-4 rounded border border-line">
                        <div className="flex flex-col border-b border-line">
                            <label className="text-[9px] font-black text-ink-secondary uppercase tracking-tighter">{t('personalInfo.lblDisplayName')}</label>
                            <input className="outline-none py-1 font-bold text-primary bg-transparent" value={formData.display_name} onChange={e => setFormData({...formData, display_name: e.target.value})} />
                        </div>
                        <div className="flex flex-col border-b border-line">
                            <label className="text-[9px] font-black text-ink-secondary uppercase tracking-tighter">{t('personalInfo.lblEmail')}</label>
                            <input className="outline-none py-1 font-bold text-primary bg-transparent" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                        </div>
                    </div>

                    <div className="flex flex-col border-b border-line group">
                        <label className="text-[9px] font-black text-primary uppercase tracking-tighter flex items-center gap-1">
                            <Building2 size={10}/> {t('personalInfo.lblDefaultOrg')}
                        </label>
                        <select disabled={!isSuperAdmin} className="outline-none py-2 font-black text-primary bg-transparent uppercase text-[11px] disabled:opacity-60 disabled:cursor-not-allowed" value={formData.org_id} onChange={e => setFormData({...formData, org_id: e.target.value, site_id: ''})}>
                            <option value="">{t('personalInfo.selectOrg')}</option>
                            {orgs.map(org => <option key={org.org_id} value={org.org_id}>{org.description}</option>)}
                        </select>
                    </div>

                    <div className="flex flex-col border-b border-line group">
                        <label className="text-[9px] font-black text-primary uppercase tracking-tighter flex items-center gap-1">
                            <MapPin size={10}/> {t('personalInfo.lblDefaultSite')}
                        </label>
                        <select disabled={!isSuperAdmin} className="outline-none py-2 font-black text-primary bg-transparent uppercase text-[11px] disabled:opacity-60 disabled:cursor-not-allowed" value={formData.site_id} onChange={e => setFormData({...formData, site_id: e.target.value})}>
                            <option value="">{t('personalInfo.selectSite')}</option>
                            {sites.map(site => <option key={site.site_id} value={site.site_id}>{site.description}</option>)}
                        </select>
                    </div>

                    <div className="flex flex-col border-b border-line group col-span-2">
                        <label className="text-[9px] font-black text-primary uppercase tracking-tighter flex items-center gap-1">
                            <Globe size={10}/> {t('personalInfo.lblDefaultLang')}
                        </label>
                        <select className="outline-none py-2 font-black text-primary bg-transparent uppercase text-[11px]" value={formData.language} onChange={e => setFormData({...formData, language: e.target.value})}>
                            <option value="en">English (US)</option>
                            <option value="ar">العربية (Arabic)</option>
                            <option value="ur">اردو (Urdu)</option>
                            <option value="fr">Français (French)</option>
                            <option value="es">Español (Spanish)</option>
                            <option value="de">Deutsch (German)</option>
                            <option value="ru">Русский (Russian)</option>
                            <option value="zh">中文 (Chinese)</option>
                            <option value="ja">日本語 (Japanese)</option>
                            <option value="it">Italiano (Italian)</option>
                            <option value="ko">한국어 (Korean)</option>
                        </select>
                    </div>

                    {/* 🎯 سكشن الـ Security & 2FA */}
                    <div className="col-span-2 mt-2">
                        <h3 className="text-[11px] font-black text-primary-dark border-b border-line pb-2 mb-4 uppercase flex items-center gap-2">
                            <ShieldCheck size={16} className="text-status-success"/> {t('personalInfo.securityTitle')}
                        </h3>

                        <div className="bg-surface-panel border border-line rounded p-5 flex flex-col gap-4">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="w-4 h-4 accent-primary"
                                    checked={formData.is_2fa_enabled}
                                    onChange={e => {
                                        const isEnabled = e.target.checked;
                                        setFormData({...formData, is_2fa_enabled: isEnabled, two_factor_method: isEnabled ? 'authenticator' : 'none'});
                                        setSetupMode(false);
                                    }}
                                />
                                <span className="font-bold text-ink text-sm">{t('personalInfo.enable2FA')}</span>
                            </label>

                            {formData.is_2fa_enabled && (
                                <div className="ml-7 flex flex-col gap-4 animate-in fade-in duration-300">
                                    <div className="flex gap-4">
                                        <label className={`flex-1 border rounded p-3 flex flex-col items-center gap-2 cursor-pointer transition-all ${formData.two_factor_method === 'authenticator' ? 'border-primary bg-primary-soft shadow-sm' : 'border-line hover:bg-surface-panel'}`}>
                                            <input type="radio" name="2fa" value="authenticator" className="hidden" checked={formData.two_factor_method === 'authenticator'} onChange={() => {setFormData({...formData, two_factor_method: 'authenticator'}); setSetupMode(false);}}/>
                                            <Smartphone size={24} className={formData.two_factor_method === 'authenticator' ? 'text-primary' : 'text-ink-secondary'}/>
                                            <span className="font-bold text-[10px] uppercase text-center">{t('personalInfo.googleAuth')}</span>
                                        </label>
                                        <label className={`flex-1 border rounded p-3 flex flex-col items-center gap-2 cursor-pointer transition-all ${formData.two_factor_method === 'email' ? 'border-primary bg-primary-soft shadow-sm' : 'border-line hover:bg-surface-panel'}`}>
                                            <input type="radio" name="2fa" value="email" className="hidden" checked={formData.two_factor_method === 'email'} onChange={() => {setFormData({...formData, two_factor_method: 'email'}); setSetupMode(false);}}/>
                                            <Mail size={24} className={formData.two_factor_method === 'email' ? 'text-primary' : 'text-ink-secondary'}/>
                                            <span className="font-bold text-[10px] uppercase text-center">{t('personalInfo.emailOTP')}</span>
                                        </label>
                                    </div>

                                    {!setupMode && (
                                        <button onClick={handleSetup2FA} disabled={setupLoading} className="bg-primary-dark text-ink-inverse py-2 rounded text-[10px] font-black uppercase hover:bg-primary-hover transition-colors w-fit px-6 flex items-center gap-2">
                                            {setupLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                            {t('personalInfo.configure2FA')}
                                        </button>
                                    )}

                                    {setupMode && (
                                        <div className="bg-surface-card border border-line rounded p-4 flex flex-col items-center gap-4 animate-in fade-in zoom-in-95">
                                            {formData.two_factor_method === 'authenticator' ? (
                                                <>
                                                    {qrUri && <QRCodeSVG value={qrUri} size={150} level="H" />}
                                                    <p className="text-[10px] text-ink-secondary text-center px-4">{t('personalInfo.scanQR')}</p>
                                                </>
                                            ) : (
                                                <div className="flex flex-col items-center gap-2 pb-2">
                                                    <div className="w-12 h-12 bg-primary-soft rounded-full flex items-center justify-center text-primary">
                                                        <Mail size={24} />
                                                    </div>
                                                    <p className="text-[11px] font-bold text-ink-secondary text-center mt-2">
                                                        {t('personalInfo.otpSentTo')} <span className="text-primary">{formData.email}</span>
                                                    </p>
                                                    <p className="text-[9px] text-ink-secondary text-center uppercase tracking-widest">{t('personalInfo.validFor10Min')}</p>
                                                </div>
                                            )}

                                            <div className="flex gap-2">
                                                <input
                                                    maxLength={6}
                                                    className="border-2 border-line rounded px-3 py-1.5 text-center font-mono text-lg tracking-widest outline-none focus:border-primary w-36"
                                                    placeholder="000000"
                                                    value={verificationCode}
                                                    onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                                                />
                                                <button onClick={handleVerify2FA} disabled={verificationCode.length !== 6 || setupLoading} className="bg-status-success text-ink-inverse px-4 font-black text-[10px] uppercase rounded hover:bg-status-success/90 disabled:opacity-50 flex items-center gap-2">
                                                    {setupLoading ? <Loader2 size={14} className="animate-spin" /> : t('personalInfo.btnVerify')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 🎯 سكشن المظهر (Appearance) — اختيار شخصي لكل مستخدم */}
                    <div className="col-span-2 mt-2">
                        <h3 className="text-[11px] font-black text-primary-dark border-b border-line pb-2 mb-4 uppercase flex items-center gap-2">
                            <Palette size={16} className="text-primary"/> {t('personalInfo.appearanceTitle', 'Appearance')}
                        </h3>

                        <div className="bg-surface-panel border border-line rounded p-5 flex flex-col gap-5">
                            <div>
                                <label className="text-[9px] font-black text-ink-secondary uppercase tracking-tighter block mb-3">
                                    {t('personalInfo.colorPackage', 'Color Package')}
                                </label>
                                <div className="grid grid-cols-4 gap-3">
                                    {PALETTES.map(p => (
                                        <button
                                            key={p.id}
                                            type="button"
                                            onClick={() => applyPalette(p.id)}
                                            className={`relative flex flex-col items-center gap-2 p-3 rounded border-2 transition-all ${formData.theme_palette === p.id ? 'border-primary bg-primary-soft shadow-sm' : 'border-line hover:bg-surface-panel'}`}
                                        >
                                            {formData.theme_palette === p.id && (
                                                <span className="absolute top-1 right-1 bg-primary text-ink-inverse rounded-full p-0.5">
                                                    <Check size={10} strokeWidth={4} />
                                                </span>
                                            )}
                                            <span className="flex gap-1">
                                                <span className="w-5 h-5 rounded-full shadow-inner" style={{ backgroundColor: p.swatch }} />
                                                <span className="w-5 h-5 rounded-full shadow-inner" style={{ backgroundColor: p.swatchAccent }} />
                                            </span>
                                            <span className="font-bold text-[10px] uppercase text-ink-secondary">{p.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-[9px] font-black text-ink-secondary uppercase tracking-tighter block mb-3">
                                    {t('personalInfo.displayMode', 'Display Mode')}
                                </label>
                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => applyMode('light')}
                                        className={`flex-1 border rounded p-3 flex items-center justify-center gap-2 cursor-pointer transition-all ${formData.theme_mode === 'light' ? 'border-primary bg-primary-soft shadow-sm' : 'border-line hover:bg-surface-panel'}`}
                                    >
                                        <Sun size={16} className={formData.theme_mode === 'light' ? 'text-primary' : 'text-ink-secondary'}/>
                                        <span className="font-bold text-[10px] uppercase">{t('personalInfo.lightMode', 'Light')}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => applyMode('dark')}
                                        className={`flex-1 border rounded p-3 flex items-center justify-center gap-2 cursor-pointer transition-all ${formData.theme_mode === 'dark' ? 'border-primary bg-primary-soft shadow-sm' : 'border-line hover:bg-surface-panel'}`}
                                    >
                                        <Moon size={16} className={formData.theme_mode === 'dark' ? 'text-primary' : 'text-ink-secondary'}/>
                                        <span className="font-bold text-[10px] uppercase">{t('personalInfo.darkMode', 'Dark')}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="p-4 bg-surface-panel border-t border-line flex justify-end gap-3 flex-shrink-0">
                    <button onClick={handleClose} className="px-6 py-2 text-[10px] font-black uppercase text-ink-secondary hover:bg-surface-card transition-all">{t('personalInfo.btnCancel')}</button>
                    <button onClick={handleSave} disabled={loading} className="bg-primary-dark text-ink-inverse px-10 py-2 rounded-sm font-black text-[10px] uppercase flex items-center gap-2 hover:bg-primary-hover shadow-lg transition-all">
                        {loading ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} 
                        {t('personalInfo.btnSave')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PersonalInformationModal;